import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Featured article — faithful rewrite of legacy molecules/FeaturedInsight (the
// latest entry hero on insights/news/solution-brief listing pages). Gradient
// band: read-time + blue title + excerpt on the left, feature image on the right.
export const featuredArticleSchema = z.object({
  minuteRead: z.string().optional(),
  title: z.string().min(1),
  excerpt: z.string().optional(),
  imageUrl: z.string().optional(),
  href: safeUrl.optional(),
});

type FeaturedArticleProps = z.infer<typeof featuredArticleSchema>;

const Band = styled.section`
  overflow: hidden;
  border-radius: 0 0 80px 0;
  background: linear-gradient(93deg, #e8efff 0%, #fff 100%);
  padding: 0 2.4rem 4.8rem;
`;

const Inner = styled.div`
  max-width: 112rem;
  margin: 0 auto;
  padding-top: 6rem;
  display: grid;
  gap: 3rem;
  align-items: center;

  @media (min-width: 62em) {
    grid-template-columns: 1fr 1fr;
    column-gap: 10rem;
    padding-top: 8rem;
    padding-bottom: 4rem;
  }
`;

const ReadTime = styled.p`
  margin: 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
`;

const Title = styled.a`
  display: block;
  margin: 1.2rem 0 0;
  color: #0a3aa8;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 2.4rem;
  text-decoration: none;

  @media (min-width: 48em) {
    font-size: 3.6rem;
  }
`;

const Excerpt = styled.p`
  margin: 1.6rem 0 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.8rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    font-size: 2rem;
  }
`;

const ImageWrap = styled.div`
  width: 100%;
  height: 30rem;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  object-fit: cover;
`;

function FeaturedArticle({ minuteRead, title, excerpt, imageUrl, href }: FeaturedArticleProps) {
  return (
    <Band>
      <Inner>
        <div>
          {minuteRead ? <ReadTime>{minuteRead} min read</ReadTime> : null}
          <Title as={href ? 'a' : 'span'} href={href}>
            {title}
          </Title>
          {excerpt ? <Excerpt>{excerpt.slice(0, 200)}</Excerpt> : null}
        </div>
        {imageUrl ? (
          <ImageWrap>
            <Img src={imageUrl} alt="" />
          </ImageWrap>
        ) : null}
      </Inner>
    </Band>
  );
}

export const featuredArticleBlock: BlockDefinition<FeaturedArticleProps> = {
  type: 'featuredArticle',
  schema: featuredArticleSchema,
  Renderer: FeaturedArticle,
};
